/**
 * @file Builds the entity profile schema, picking the trustee or the
 * other-entity inputs based on the membership-interests title.
 */
import { TYPE_RADIO } from "@components/Form";

import {
  INVESTMENT_PROFILE_TYPE_ENTITY,
  ENTITY_MEMBERSHIP_INTEREST_TRUSTEE,
  ENTITY_MEMBERSHIP_INTEREST_OTHER,
  ENTITY_MEMBERSHIP_INTERESTS_TITLES_MAP,
} from "@components/Domain";

import computeProfileInput from "./computeProfileInput";
import computeLiabilityInputs from "./computeLiabilityInputs";
import getProfileEntityTrusteeSchema from "./getProfileEntityTrusteeSchema";
import getProfileEntityInputsMap from "./getProfileEntityInputsMap";
import getProfileEntityOtherSchema from "./getProfileEntityOtherSchema";
import type {
  Input,
  Profile,
  Organization,
  UpdateSchema,
  ProfileFields,
  FormProfileEntity,
} from "../types";

/**
 * Builds the entity profile inputs, hiding them behind a profile picker
 * when reusable profiles exist, then branching into the trustee or the
 * other-entity fields once a membership-interests title is picked.
 */
const getProfileEntitySchema = (
  organization: Organization,
  updateSchema: UpdateSchema,
  isEdit: boolean,
  profileNameExtraWarning: string | null,
  profile: FormProfileEntity = {},
  existingProfiles: Profile[] = [],
): Input[] => {
  const {
    membershipInterestsTitle,
    profileName,
  } = profile;

  const inputPropsMap = getProfileEntityInputsMap();

  const isTrustee = membershipInterestsTitle === ENTITY_MEMBERSHIP_INTEREST_TRUSTEE;
  const isEntity = membershipInterestsTitle === ENTITY_MEMBERSHIP_INTEREST_OTHER;

  /**
   * Derives whether the selected profile should provide an EIN, based on
   * whether it already has an EIN or an SSN.
   */
  const profileExtra = (selectedProfile: ProfileFields = {}) => {
    const { einNumber, ssnNumber } = selectedProfile as FormProfileEntity;

    let shouldProvideEin;

    if (einNumber) {
      shouldProvideEin = true;
    }

    if (ssnNumber) {
      shouldProvideEin = false;
    }

    return { _shouldProvideEin: shouldProvideEin };
  };

  const liabilityInputs = computeLiabilityInputs(updateSchema, organization);
  const profileSelectInput = computeProfileInput(INVESTMENT_PROFILE_TYPE_ENTITY, updateSchema, existingProfiles, profileExtra);
  const hasProfiles = existingProfiles.filter(({ disabled }) => !disabled).length > 0;

  const hiddenAllFields = Object.values(inputPropsMap);
  const shouldHideAnotherFields = hasProfiles && !profileName && !isEdit;

  if (shouldHideAnotherFields) {
    return [
      ...profileSelectInput,
      ...hiddenAllFields,
    ];
  }

  const commonInputs: Input[] = [
    ...profileSelectInput,
    ...liabilityInputs,
    {
      name: "membershipInterestsTitle",
      type: TYPE_RADIO,
      label: "Title of membership interests",
      required: true,
      inputProps: {
        vertical: "true",
        /** Refreshes the schema after the membership-interests title changes. */
        onChange: () => updateSchema(),
      },
      options: Object.keys(ENTITY_MEMBERSHIP_INTERESTS_TITLES_MAP)
        .map((value) => ({ value, label: ENTITY_MEMBERSHIP_INTERESTS_TITLES_MAP[value] })),
    },
  ];

  let inputs: Input[] = [
    ...commonInputs,
    ...[
      inputPropsMap.name,
      inputPropsMap.entityType,
      inputPropsMap.title,
      inputPropsMap.address,
      inputPropsMap.isDisregardedForTaxPurposes,
      inputPropsMap.taxFilingStatus,
      inputPropsMap.hasEinNumber,
      inputPropsMap.einNumber,
      inputPropsMap.ssnNumber,
      inputPropsMap.isRetirementPlan,
      inputPropsMap.beneficialOwnerName,
      inputPropsMap.beneficialOwnerAddress,
      inputPropsMap.beneficialOwnerEinNumber,
      inputPropsMap.beneficialOwnerSsnNumber,
      inputPropsMap.beneficialOwnerEntityType,
      inputPropsMap.beneficialOwnerTaxFilingStatus,
      inputPropsMap.beneficialOwnerCustodianEinNumber,
      inputPropsMap.isUsPerson,
      inputPropsMap.isUsCitizen,
      inputPropsMap.hasOtherUsResidenceState,
      inputPropsMap.residenceState,
      inputPropsMap.hasSsn,
    ],
  ];

  if (isTrustee) {
    const trusteeInputs = getProfileEntityTrusteeSchema(organization, updateSchema, profileNameExtraWarning, profile);

    inputs = [
      ...commonInputs,
      ...trusteeInputs,
      ...[
        inputPropsMap.entityType,
        inputPropsMap.title,
        inputPropsMap.taxFilingStatus,
        inputPropsMap.ssnNumber,
        inputPropsMap.isRetirementPlan,
      ],
    ];
  }

  if (isEntity) {
    const entityInputs = getProfileEntityOtherSchema(organization, updateSchema, profileNameExtraWarning, profile);

    inputs = [
      ...commonInputs,
      ...entityInputs,
      ...[
        inputPropsMap.ssnNumber,
      ],
    ];
  }

  return inputs;
};

export default getProfileEntitySchema;
